import React from "react";
import { Modal, Button, FormInput, FormTextarea } from "@/components/ui";
import { useDispatchLogic } from "../hooks/useDispatchLogic";

import { Truck, User, Hash, FileText, IndianRupee, Plus, Trash2 } from "lucide-react";

const numberToWords = (num) => {
  if (!num || isNaN(num) || num <= 0) return "";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 !== 0 ? " and " + convert(n % 100) : "");
    if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 !== 0 ? " " + convert(n % 1000) : "");
    if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 !== 0 ? " " + convert(n % 100000) : "");
    return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 !== 0 ? " " + convert(n % 10000000) : "");
  };
  return convert(Math.floor(num)) + " Rupees Only";
};

export const DispatchModal = ({ isOpen, onClose, onConfirm, requestId, request }) => {
  const {
    driverName,
    setDriverName,
    vehicleNo,
    setVehicleNo,
    description,
    setDescription,
    wayBillNo,
    setWayBillNo,
    amount,
    setAmount,
    items, handleAddItem, handleRemoveItem, handleItemChange,
    cgstRate, setCgstRate,
    sgstRate, setSgstRate,
    subTotal, cgstAmount, sgstAmount, grandTotal,
    officeAddress, setOfficeAddress,
    shipToAddress, setShipToAddress,
    billToAddress, setBillToAddress,
    sameAsShipTo, setSameAsShipTo,
    invoiceNo, setInvoiceNo,
    documentNo, setDocumentNo,
    handleSubmit
  } = useDispatchLogic({ requestId, request, onConfirm });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Dispatch Materials"
      size="lg"
      bodyClassName="overflow-y-auto flex-1 px-4 pb-4 pt-2 sm:px-5 sm:pb-3 sm:pt-2"
      footer={
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:flex-1 h-10 rounded-lg text-[12px] font-bold"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="w-full sm:flex-1 h-10 rounded-lg text-[12px] font-bold"
          >
            Confirm Dispatch
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2.5 font-poppins">
        <div className="bg-brand-blue/5 p-3 rounded-xl border border-brand-blue/10 flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue">
            <Truck size={18} />
          </div>
          <div className="flex flex-col gap-0.5">
            <h4 className="text-[13px] font-bold text-slate-800 leading-none">Assign Delivery Details</h4>
            <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest leading-none mt-0.5">Request: {requestId}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Driver Name"
            required
            placeholder="Driver full name"
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
            icon={<User className="w-3.5 h-3.5 text-slate-400" />}
          />

          <FormInput
            label="Vehicle Number"
            required
            placeholder="AB-12-CD-3456"
            value={vehicleNo}
            onChange={(e) => setVehicleNo(e.target.value)}
            icon={<Hash className="w-3.5 h-3.5 text-slate-400" />}
            inputClassName="uppercase font-bold tracking-wider"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            icon={<FileText size={15} />}
            label="Way Bill No"
            placeholder="E.G. WB12345678"
            value={wayBillNo}
            onChange={(e) => setWayBillNo(e.target.value)}
          />
          <FormInput
            icon={<FileText size={15} />}
            label="Waybill Document No"
            placeholder="E.G. DOC98765432"
            value={documentNo}
            onChange={(e) => setDocumentNo(e.target.value)}
          />

          <FormInput
            icon={<FileText size={15} />}
            label="Invoice No"
            placeholder="E.G. INV123456"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
          />

          <FormInput
            label="Notes (Optional)"
            placeholder="Any additional delivery instructions..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Amount field commented out as requested
          <div className="flex flex-col gap-1">
            <FormInput
              label="Amount"
              placeholder="e.g. 0.00 or -"
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              icon={<IndianRupee className="w-3.5 h-3.5 text-slate-400" />}
            />
            {amount && amount > 0 ? (
              <span className="text-[10px] text-emerald-600 font-medium px-1">
                {numberToWords(Number(amount))}
              </span>
            ) : null}
          </div>
          */}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
          <FormTextarea
            label="Ship To Address (Customer)"
            placeholder="Default customer address..."
            value={shipToAddress}
            onChange={(e) => setShipToAddress(e.target.value)}
            rows={2}
          />
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-slate-500 block">Bill To Address</label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input 
                  type="checkbox" 
                  className="w-3 h-3 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  checked={sameAsShipTo}
                  onChange={(e) => setSameAsShipTo(e.target.checked)}
                />
                <span className="text-[10px] text-slate-500 font-medium">Same as Ship To</span>
              </label>
            </div>
            <textarea
              className={`w-full border rounded-md px-3 py-2.5 text-[13px] text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:ring-1 transition-all bg-white leading-relaxed border-slate-200 focus:border-brand-blue focus:ring-brand-blue/30 ${sameAsShipTo ? 'bg-slate-100 cursor-not-allowed opacity-60' : ''}`}
              placeholder="Enter bill to address..."
              value={billToAddress}
              onChange={(e) => setBillToAddress(e.target.value)}
              disabled={sameAsShipTo}
              rows={2}
            ></textarea>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-[12px] font-semibold text-slate-700">Dispatch Items</label>
            <button 
              type="button" 
              onClick={handleAddItem}
              className="text-[11px] font-bold text-brand-blue flex items-center gap-1 hover:text-blue-700 transition-colors bg-brand-blue/5 px-2 py-1 rounded-md"
            >
              <Plus size={12} /> Item
            </button>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="max-h-[140px] overflow-y-auto custom-scrollbar">
              <table className="w-full text-left text-[12px] relative">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-3 py-2 font-medium w-12 text-center">S.No</th>
                  <th className="px-3 py-2 font-medium">Item Description</th>
                  <th className="px-3 py-2 font-medium w-24">HSN</th>
                  <th className="px-3 py-2 font-medium w-24">Quantity</th>
                  <th className="px-3 py-2 font-medium w-28">Price</th>
                  <th className="px-3 py-2 font-medium w-28">Total</th>
                  <th className="px-3 py-2 font-medium w-12 text-center">Act</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item, idx) => (
                  <tr key={item.id} className="bg-white">
                    <td className="px-3 py-2 text-center text-slate-500 font-medium">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <input 
                        type="text" 
                        className="w-full bg-transparent border-0 p-0 text-[12px] outline-none focus:outline-none focus:ring-0 focus:border-transparent shadow-none text-slate-800 placeholder:text-slate-300"
                        placeholder="Item name..."
                        value={item.description}
                        onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input 
                        type="text" 
                        className="w-full bg-transparent border-0 p-0 text-[12px] outline-none focus:outline-none focus:ring-0 focus:border-transparent shadow-none text-slate-800 placeholder:text-slate-300"
                        placeholder="HSN"
                        value={item.hsnCode}
                        onChange={(e) => handleItemChange(item.id, 'hsnCode', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input 
                        type="number" 
                        min="1"
                        className="w-full bg-transparent border-0 p-0 text-[12px] outline-none focus:outline-none focus:ring-0 focus:border-transparent shadow-none text-slate-800"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input 
                        type="number" 
                        min="0"
                        className="w-full bg-transparent border-0 p-0 text-[12px] outline-none focus:outline-none focus:ring-0 focus:border-transparent shadow-none text-slate-800"
                        value={item.price}
                        onChange={(e) => handleItemChange(item.id, 'price', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-700">
                      ₹ {(Number(item.quantity) * Number(item.price)).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button 
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        disabled={items.length === 1}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          <div className="flex justify-end mt-2">
            <div className="w-64 flex flex-col gap-1.5 text-[12px]">
              <div className="flex items-center justify-between px-2 text-slate-600 border-b border-slate-100 pb-1.5 mb-1">
                <span>Sub Total:</span>
                <span className="font-semibold text-slate-800">₹ {subTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-2 text-slate-600 gap-2">
                <span className="whitespace-nowrap">CGST (%):</span>
                <input 
                  type="number" 
                  className="w-16 h-6 text-right border border-slate-200 rounded text-[11px] focus:ring-1 focus:ring-brand-blue px-1"
                  value={cgstRate}
                  onChange={(e) => setCgstRate(e.target.value)}
                />
                <span className="w-20 text-right font-medium text-slate-700">₹ {cgstAmount.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-2 text-slate-600 gap-2">
                <span className="whitespace-nowrap">SGST (%):</span>
                <input 
                  type="number" 
                  className="w-16 h-6 text-right border border-slate-200 rounded text-[11px] focus:ring-1 focus:ring-brand-blue px-1"
                  value={sgstRate}
                  onChange={(e) => setSgstRate(e.target.value)}
                />
                <span className="w-20 text-right font-medium text-slate-700">₹ {sgstAmount.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-2 text-slate-600 border-t border-slate-100 pt-1.5 mt-1 mb-1">
                <span>Total Tax:</span>
                <span className="font-semibold text-slate-700">₹ {(cgstAmount + sgstAmount).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-2 py-1.5 mt-1 bg-slate-100 rounded-md border border-slate-200 text-slate-800 font-bold">
                <span>Final Amount:</span>
                <span className="text-brand-blue">₹ {grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
